import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { BrandMark } from "@/components/brand/BrandMark";

const PRIVACY_TEXT =
  "A Doxa School utilizará seus dados para gestão da turma, controle de presença, acompanhamento de progresso, envio de avisos, organização de materiais e acompanhamento interno da formação. Seus dados serão acessados apenas por pessoas autorizadas.";

export default function FirstAccess() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { firstAccess } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then(({ data }) => setInvite(data)).catch((err) => {
      toast.error(formatApiError(err));
    }).finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) return toast.error("Você precisa aceitar o aviso de uso de dados.");
    if (pw.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres.");
    if (pw !== pw2) return toast.error("As senhas não conferem.");
    setSubmitting(true);
    try {
      const u = await firstAccess({ invite_token: token, password: pw, privacy_accepted: true });
      toast.success("Senha criada com sucesso!");
      nav(u.role === "STUDENT" ? "/aluno" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B655E]">Carregando…</div>;
  if (!invite) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="doxa-card p-8 max-w-md w-full text-center">
        <p className="font-display text-xl font-bold">Convite inválido</p>
        <p className="text-[#6B655E] mt-2 text-sm">Solicite à equipe Doxa um novo link.</p>
      </div>
    </div>
  );

  const canSubmit = accepted && pw.length >= 6 && pw === pw2 && !submitting;

  return (
    <div className="min-h-screen bg-[#F8F3EA] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <BrandMark size="lg" />
          <div>
            <p className="font-display text-xl font-bold">Doxa School</p>
            <p className="text-xs text-[#6B655E]">Primeiro acesso</p>
          </div>
        </div>

        <div className="doxa-card p-6 md:p-8">
          <h1 className="font-display text-2xl md:text-3xl font-bold">Olá, {invite.name.split(" ")[0]}!</h1>
          <p className="mt-2 text-[#6B655E]">Antes de criar sua senha, leia e aceite o aviso abaixo.</p>

          <div className="mt-6 p-4 rounded-xl bg-[#F6EFE4] border border-[#E0D9CD]">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-[#F75A1B] shrink-0 mt-0.5" />
              <p className="text-sm text-[#1F1F1F] leading-relaxed">{PRIVACY_TEXT}</p>
            </div>
          </div>

          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="privacy-accept"
              className="mt-1 w-4 h-4 accent-[#F75A1B]"
            />
            <span className="text-sm">Li e aceito o aviso de uso de dados.</span>
          </label>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Crie uma senha (mín. 6 caracteres)</label>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                disabled={!accepted}
                data-testid="first-password"
                className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B] disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Confirme a senha</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                disabled={!accepted}
                data-testid="first-password-confirm"
                className="w-full px-3 py-2.5 bg-white border border-[#E0D9CD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F75A1B] disabled:opacity-60"
              />
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="first-submit"
              className="doxa-btn-primary w-full py-3 inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Criar senha e acessar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
